/**
 * Scan Bills Step 2 — review and select detected bills
 */

import React, { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useRegion } from '../../../contexts/RegionContext';
import type { DetectedBillCandidate } from '../../../types/bill-scan.types';
import type { BillFrequency } from '../../../types/bill.types';

const FREQUENCY_LABELS: Record<BillFrequency, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  one_off: 'One-off',
};

/** Format due date as day + month only (e.g. "15 Aug"). */
export function formatDueDayMonth(dueDate: string): string {
  try {
    return format(parseISO(dueDate), 'd MMM');
  } catch {
    return dueDate;
  }
}

interface ScanBillsStep2ReviewProps {
  candidates: DetectedBillCandidate[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (selectAll: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}

export function ScanBillsStep2Review({
  candidates,
  selectedIds,
  onToggle,
  onToggleAll,
  onBack,
  onNext,
}: ScanBillsStep2ReviewProps): React.ReactElement {
  const { formatCurrency } = useRegion();

  const selectable = useMemo(
    () => candidates.filter((c) => !c.alreadyExists),
    [candidates]
  );

  const allSelected =
    selectable.length > 0 && selectable.every((c) => selectedIds.has(c.id));
  const someSelected = selectable.some((c) => selectedIds.has(c.id));

  if (candidates.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            No recurring bills found
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            We couldn&apos;t identify recurring bills in the selected transactions. Try a wider
            date range or different accounts.
          </p>
        </div>
        <div className="flex justify-between pt-2">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Select bills to track
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Choose which recurring payments to create as bill reminders.
        </p>
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th scope="col" className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  disabled={selectable.length === 0}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  aria-label="Select all bills"
                />
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Bill
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Frequency
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Due
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Category
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {candidates.map((c) => {
              const disabled = !!c.alreadyExists;
              const checked = selectedIds.has(c.id);
              return (
                <tr
                  key={c.id}
                  className={
                    disabled
                      ? 'opacity-60 bg-gray-50 dark:bg-gray-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                  }
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={checked && !disabled}
                      disabled={disabled}
                      onChange={() => onToggle(c.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      aria-label={`Select ${c.name}`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {c.name}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {c.alreadyExists && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          Already tracked
                        </span>
                      )}
                      {c.occurrenceCount != null && c.occurrenceCount > 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {c.occurrenceCount} payment
                          {c.occurrenceCount === 1 ? '' : 's'} detected
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {formatCurrency(c.amount)}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {FREQUENCY_LABELS[c.frequency] ?? c.frequency}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {formatDueDayMonth(c.dueDate)}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {c.categoryName ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={selectedIds.size === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
        >
          Create {selectedIds.size > 0 ? `${selectedIds.size} ` : ''}bill
          {selectedIds.size === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  );
}
